import React, { useContext, useState } from 'react';
import {
    Drawer, List, ListItemButton, ListItemIcon, ListItemText, Box, Typography, Chip, Collapse
} from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import {
    Dashboard, LocalShipping, Replay, Settings, Autorenew, IntegrationInstructions, LocationOn, Description,
    Email, Sms, Public, SettingsBackupRestore, Help, MonetizationOn, Store, ExpandLess, ExpandMore,
    BusinessCenterSharp, LocalShippingSharp, MoneyOutlined,
    GroupAdd,
    Scale,
    MapsHomeWorkOutlined,
    DesignServices,
    RequestQuoteOutlined,
    NotesRounded,
    CreditCard,
    RecordVoiceOver,
    Scanner
} from '@mui/icons-material';
import '../../assets/styles/sidebar.scss';
import { CommonContext } from '../../context';

const menuItems = [
    {
        section: '', items: [
            { text: 'Dashboard', icon: <Dashboard />, path: '/' },
            {
                text: 'Orders', icon: <LocalShipping />, path: '/orders', isDropdown: true, subItems: [
                    { text: 'B2C', path: '/orders/b2c' },
                    { text: 'B2B', path: '/orders/b2b' },
                ]
            },
            {
                text: 'Merchants', icon: <BusinessCenterSharp />, path: '/merchants', isDropdown: true, subItems: [
                    { text: 'Registered', path: '/merchants/registered' },
                    { text: 'Signup', path: '/merchants/signup' },
                ]
            },
            { text: 'Courier', icon: <LocalShippingSharp />, path: '/couriers' },
            { text: 'Branch', icon: <LocationOn />, path: '/branches' },
            { text: 'Warehouse', icon: <Store />, path: '/warehouse' },
            {
                text: 'Freight', icon: <MoneyOutlined />, path: '/freight', isDropdown: true, subItems: [
                    { text: 'B2C', path: '/freight/b2c' },
                    { text: 'B2B', path: '/freight/b2b' },
                ]
            },
            {
                text: 'NDR', icon: <Replay />, path: '/ndr', isDropdown: true, subItems: [
                    { text: 'B2C', path: '/ndr/b2c' },
                    { text: 'B2B', path: '/ndr/b2b' },
                ]
            },
            {
                text: 'Weight', icon: <Scale />, path: '/weight', isDropdown: true, subItems: [
                    { text: 'B2C', path: '/weight/b2c' },
                    { text: 'B2B', path: '/weight/b2b' },
                ]
            },
            {
                text: 'Invoices', icon: <Description />, path: '/invoices', isDropdown: true, subItems: [
                    { text: 'B2C', path: '/invoices/b2c' },
                    { text: 'B2B', path: '/invoices/b2b' },
                ]
            },

            { text: 'Credit Notes', icon: <NotesRounded />, path: '/credit-notes' },
            { text: 'Users', icon: <GroupAdd />, path: '/users' },
            { text: 'Serviceability', icon: <DesignServices />, path: '/serviceability' },
            { text: 'Trackings', icon: <MapsHomeWorkOutlined />, path: '/trackings' },
            { text: 'Pickup Requests', icon: <RequestQuoteOutlined />, path: '/pickup-requests' },
            { text: 'Remittance', icon: <CreditCard />, path: '/remittance' },
            { text: 'IVR', icon: <RecordVoiceOver />, path: '/ivr' },
            { text: 'Scan Status', icon: <Scanner />, path: '/scan-status' },
        ]
    }
];

const Sidebar = () => {
    const { user } = useContext(CommonContext);
    const location = useLocation();

    const [openDropdown, setOpenDropdown] = useState({});

    const handleToggleDropdown = (key) => {
        setOpenDropdown((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };
    const HIDE_FOR_MERCHANT = [
        "Merchants",
        "Courier",
        "Branch",
        "IVR",
        "Scan Status",
        "Users"
    ];

    return (
        <Drawer
            variant="permanent"
            sx={{
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                    width: 180,
                    boxSizing: 'border-box',
                    backgroundColor: '#f5f8f8',
                    borderRight: '1px solid #ddd'
                }
            }}
        >
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" fontSize={15}>
                    Gaint Logistics <br /> Services
                </Typography>
            </Box>
            <Box className="custom-scrollbar" sx={{ overflowY: 'auto', height: 'calc(100vh - 64px)' }}>
                {menuItems.map(({ section, items }, idx) => (
                    <Box key={idx}>
                        {section && (
                            <Typography variant="caption" sx={{ px: 2, pt: 2, pb: 1, color: '#555' }}>
                                {section}
                            </Typography>
                        )}
                        <List disablePadding>
                            {items
                                .filter(({ text }) => {
                                    if (user?.user_type === "Merchant") {
                                        return !HIDE_FOR_MERCHANT.includes(text);
                                    }
                                    return true;
                                })
                                .map(({ text, icon, path, chip, isDropdown, subItems }) => (
                                    <React.Fragment key={text}>
                                        {isDropdown ? (
                                            <>
                                                <ListItemButton
                                                    onClick={() => handleToggleDropdown(path)}
                                                    selected={location.pathname.startsWith(path)}
                                                    sx={{
                                                        "&.Mui-selected": {
                                                            backgroundColor: "#dff5f0",
                                                            color: "#004d40",
                                                            borderLeft: "4px solid #2e7d32",
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
                                                    <ListItemText primary={text} />
                                                    {openDropdown[path] ? <ExpandLess /> : <ExpandMore />}
                                                </ListItemButton>

                                                <Collapse in={openDropdown[path]} timeout="auto" unmountOnExit>
                                                    <List component="div" disablePadding>
                                                        {subItems.map((sub) => (
                                                            <ListItemButton
                                                                key={sub.text}
                                                                component={NavLink}
                                                                to={sub.path}
                                                                selected={location.pathname === sub.path}
                                                                sx={{
                                                                    pl: 6,
                                                                    "&.Mui-selected": {
                                                                        backgroundColor: "#dff5f0",
                                                                        color: "#004d40",
                                                                        borderLeft: "4px solid #2e7d32",
                                                                    }
                                                                }}
                                                            >
                                                                <ListItemText primary={sub.text} />
                                                            </ListItemButton>
                                                        ))}
                                                    </List>
                                                </Collapse>
                                            </>
                                        ) : (
                                            <ListItemButton
                                                component={NavLink}
                                                to={path}
                                                selected={location.pathname === path}
                                                sx={{
                                                    "&.Mui-selected": {
                                                        backgroundColor: "#dff5f0",
                                                        color: "#004d40",
                                                        borderLeft: "4px solid #2e7d32",
                                                    }
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
                                                <ListItemText primary={text} />
                                                {chip && <Chip label={chip} size="small" color="success" />}
                                            </ListItemButton>
                                        )}
                                    </React.Fragment>
                                ))}
                        </List>
                    </Box>
                ))}
            </Box>
        </Drawer>
    );
};

export default Sidebar;
